import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import Layout, { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  UserPlus,
  PencilSimple,
  Trash,
  Key,
  MapPin,
  ClockCounterClockwise,
  ShieldStar,
  User,
  UserCircle,
  ArrowsClockwise,
  Warning,
} from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";

// ─── API helpers ──────────────────────────────────────────────────────────────

const usersApi = {
  list: ()                           => api.get("/users"),
  create: (body)                     => api.post("/users", body),
  update: (id, body)                 => api.patch(`/users/${id}`, body),
  resetPassword: (id, new_password)  => api.post(`/users/${id}/reset-password`, { new_password }),
  delete: (id)                       => api.delete(`/users/${id}`),
  loginHistory: (id)                 => api.get(`/users/${id}/login-history`),
};

// ─── Constants ────────────────────────────────────────────────────────────────

const ROLES = ["super_admin", "admin", "user"];

const ROLE_COLORS = {
  super_admin: "bg-purple-500/10 text-purple-400 border-purple-500/30",
  admin:       "bg-blue-500/10 text-blue-400 border-blue-500/30",
  user:        "bg-slate-500/10 text-slate-400 border-slate-500/30",
};

const ROLE_ICONS = {
  super_admin: ShieldStar,
  admin:       UserCircle,
  user:        User,
};

const STATUS_COLORS = {
  active:    "bg-green-500/10 text-green-400 border-green-500/30",
  suspended: "bg-red-500/10 text-red-400 border-red-500/30",
};

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function UsersPage() {
  const [users, setUsers]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [createModal, setCreate]  = useState(false);
  const [editUser, setEditUser]   = useState(null);
  const [resetUser, setResetUser] = useState(null);
  const [deleteUser, setDeleteU]  = useState(null);
  const [historyUser, setHistory] = useState(null);   // {user, history:[]}
  const [saving, setSaving]       = useState(false);
  const [deleting, setDeleting]   = useState(false);
  const [histLoading, setHistL]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await usersApi.list();
      setUsers(res.data.users || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openHistory = async (user) => {
    setHistL(true);
    setHistory({ user, history: [] });
    try {
      const res = await usersApi.loginHistory(user.id);
      setHistory({ user, history: res.data.history || [] });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setHistL(false);
    }
  };

  return (
    <Layout>
      <div data-testid="users-page">
        <PageHeader
          eyebrow="Admin"
          title="User Management"
          description="Manage accounts, roles, and view login activity."
          actions={
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={load} disabled={loading}>
                <ArrowsClockwise size={14} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </Button>
              <Button size="sm" onClick={() => setCreate(true)} data-testid="create-user-btn">
                <UserPlus size={15} className="mr-1.5" />
                New User
              </Button>
            </div>
          }
        />

        {/* ── User Table ─────────────────────────────────────────────────── */}
        <div className="rounded-xl border border-border overflow-hidden" data-testid="users-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40 text-muted-foreground text-xs uppercase tracking-wide">
                <th className="text-left px-4 py-3">User</th>
                <th className="text-left px-4 py-3">Role</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Last Login IP</th>
                <th className="text-left px-4 py-3">Location</th>
                <th className="text-left px-4 py-3">Last Seen</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground text-sm">Loading…</td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground text-sm">No users found</td></tr>
              ) : (
                users.map((u) => {
                  const RoleIcon = ROLE_ICONS[u.role] || User;
                  const last = u.login_history?.[0];
                  const location = last
                    ? [last.city, last.country].filter(Boolean).join(", ") || last.country_code || "—"
                    : "—";
                  return (
                    <tr
                      key={u.id}
                      className="border-b border-border last:border-0 hover:bg-muted/20 transition-colors"
                      data-testid={`user-row-${u.id}`}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded-full bg-muted grid place-items-center text-xs font-semibold flex-shrink-0">
                            {(u.name || u.email)[0].toUpperCase()}
                          </div>
                          <div>
                            <div className="font-medium">{u.name}</div>
                            <div className="text-xs text-muted-foreground">{u.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border ${ROLE_COLORS[u.role] || ROLE_COLORS.user}`}>
                          <RoleIcon size={11} />
                          {u.role}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${STATUS_COLORS[u.status] || STATUS_COLORS.active}`}>
                          {u.status || "active"}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                        {last?.ip || "—"}
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">
                        <div className="flex items-center gap-1">
                          {last?.country_code && (
                            <span className="font-semibold text-foreground">{last.country_code}</span>
                          )}
                          {location !== "—" && (
                            <span className="text-muted-foreground truncate max-w-[120px]">{last?.city || location}</span>
                          )}
                          {!last && "—"}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">
                        {last?.timestamp ? new Date(last.timestamp).toLocaleString() : "Never"}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost" size="sm" className="h-7 px-2 text-xs text-muted-foreground"
                            data-testid={`user-history-btn-${u.id}`}
                            onClick={() => openHistory(u)}
                            title="Login history"
                          >
                            <ClockCounterClockwise size={13} />
                          </Button>
                          <Button
                            variant="ghost" size="sm" className="h-7 px-2 text-xs"
                            data-testid={`user-edit-btn-${u.id}`}
                            onClick={() => setEditUser(u)}
                            title="Edit user"
                          >
                            <PencilSimple size={13} />
                          </Button>
                          <Button
                            variant="ghost" size="sm" className="h-7 px-2 text-xs"
                            data-testid={`user-reset-btn-${u.id}`}
                            onClick={() => setResetUser(u)}
                            title="Reset password"
                          >
                            <Key size={13} />
                          </Button>
                          <Button
                            variant="ghost" size="sm"
                            className="h-7 px-2 text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            data-testid={`user-delete-btn-${u.id}`}
                            onClick={() => setDeleteU(u)}
                            title="Delete user"
                          >
                            <Trash size={13} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Modals */}
        <CreateUserModal
          open={createModal}
          onClose={() => setCreate(false)}
          onCreated={(u) => { setUsers((p) => [...p, u]); setCreate(false); }}
        />
        {editUser && (
          <EditUserModal
            user={editUser}
            open={!!editUser}
            onClose={() => setEditUser(null)}
            onSaved={(updated) => {
              setUsers((p) => p.map((u) => u.id === updated.id ? updated : u));
              setEditUser(null);
            }}
          />
        )}
        {resetUser && (
          <ResetPasswordModal
            user={resetUser}
            open={!!resetUser}
            onClose={() => setResetUser(null)}
          />
        )}
        {deleteUser && (
          <DeleteConfirmModal
            user={deleteUser}
            open={!!deleteUser}
            deleting={deleting}
            onClose={() => setDeleteU(null)}
            onConfirm={async () => {
              setDeleting(true);
              try {
                await usersApi.delete(deleteUser.id);
                setUsers((p) => p.filter((u) => u.id !== deleteUser.id));
                setDeleteU(null);
                toast.success("User deleted");
              } catch (e) {
                toast.error(formatApiError(e.response?.data?.detail));
              } finally {
                setDeleting(false);
              }
            }}
          />
        )}
        {historyUser && (
          <LoginHistoryModal
            user={historyUser.user}
            history={historyUser.history}
            loading={histLoading}
            open={!!historyUser}
            onClose={() => setHistory(null)}
          />
        )}
      </div>
    </Layout>
  );
}

// ─── Create User Modal ────────────────────────────────────────────────────────

function CreateUserModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ email: "", name: "", role: "admin", password: "" });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!form.email || !form.name || !form.password) {
      toast.error("Email, name, and password are required");
      return;
    }
    setSaving(true);
    try {
      const res = await usersApi.create(form);
      toast.success(`User ${form.email} created`);
      onCreated(res.data);
      setForm({ email: "", name: "", role: "admin", password: "" });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="create-user-modal">
        <DialogHeader>
          <DialogTitle>Create New User</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Full Name</Label>
            <Input placeholder="John Doe" value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
          </div>
          <div className="space-y-1.5">
            <Label>Email</Label>
            <Input type="email" placeholder="user@example.com" value={form.email}
              onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} />
          </div>
          <div className="space-y-1.5">
            <Label>Role</Label>
            <Select value={form.role} onValueChange={(v) => setForm((p) => ({ ...p, role: v }))}>
              <SelectTrigger data-testid="create-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Password</Label>
            <Input type="password" placeholder="Min 8 characters" value={form.password}
              onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving} data-testid="create-user-submit">
            {saving ? "Creating…" : "Create User"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Edit User Modal ──────────────────────────────────────────────────────────

function EditUserModal({ user, open, onClose, onSaved }) {
  const [form, setForm] = useState({ name: user.name, role: user.role, status: user.status || "active" });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const res = await usersApi.update(user.id, form);
      toast.success("User updated");
      onSaved(res.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="edit-user-modal">
        <DialogHeader>
          <DialogTitle>Edit User — {user.email}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Full Name</Label>
            <Input value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
          </div>
          <div className="space-y-1.5">
            <Label>Role</Label>
            <Select value={form.role} onValueChange={(v) => setForm((p) => ({ ...p, role: v }))}>
              <SelectTrigger data-testid="edit-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm((p) => ({ ...p, status: v }))}>
              <SelectTrigger data-testid="edit-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="suspended">Suspended</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving} data-testid="edit-user-submit">
            {saving ? "Saving…" : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Reset Password Modal ─────────────────────────────────────────────────────

function ResetPasswordModal({ user, open, onClose }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm]   = useState("");
  const [saving, setSaving]     = useState(false);

  const handleSubmit = async () => {
    if (password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (password !== confirm)  { toast.error("Passwords do not match"); return; }
    setSaving(true);
    try {
      await usersApi.resetPassword(user.id, password);
      toast.success(`Password reset for ${user.email}`);
      setPassword(""); setConfirm("");
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-sm" data-testid="reset-password-modal">
        <DialogHeader>
          <DialogTitle>Reset Password</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground -mt-2">
          Setting a new password for <span className="font-medium text-foreground">{user.email}</span>
        </p>
        <div className="space-y-3 py-2">
          <div className="space-y-1.5">
            <Label>New Password</Label>
            <Input type="password" placeholder="Min 8 characters" value={password}
              data-testid="new-password-input"
              onChange={(e) => setPassword(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Confirm Password</Label>
            <Input type="password" placeholder="Repeat password" value={confirm}
              data-testid="confirm-password-input"
              onChange={(e) => setConfirm(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving} data-testid="reset-password-submit">
            {saving ? "Saving…" : "Reset Password"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Delete Confirm Modal ─────────────────────────────────────────────────────

function DeleteConfirmModal({ user, open, deleting, onClose, onConfirm }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-sm" data-testid="delete-user-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-400">
            <Warning size={18} />
            Delete User
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          Are you sure you want to delete <span className="font-semibold text-foreground">{user.email}</span>?
          This action cannot be undone.
        </p>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={deleting}
            data-testid="delete-user-confirm"
          >
            {deleting ? "Deleting…" : "Delete User"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Login History Modal ──────────────────────────────────────────────────────

function LoginHistoryModal({ user, history, loading, open, onClose }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl" data-testid="login-history-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClockCounterClockwise size={18} />
            Login History — {user.email}
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="py-10 text-center text-muted-foreground text-sm">Loading…</div>
        ) : history.length === 0 ? (
          <div className="py-10 text-center text-muted-foreground text-sm">No login history recorded yet</div>
        ) : (
          <div className="rounded-lg border border-border overflow-hidden max-h-[400px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-muted-foreground uppercase tracking-wide">
                  <th className="text-left px-3 py-2.5">#</th>
                  <th className="text-left px-3 py-2.5 whitespace-nowrap">Timestamp</th>
                  <th className="text-left px-3 py-2.5">IP Address</th>
                  <th className="text-left px-3 py-2.5">Country</th>
                  <th className="text-left px-3 py-2.5">City</th>
                  <th className="text-left px-3 py-2.5">ISP / Org</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h, i) => (
                  <tr key={i} className="border-b border-border last:border-0 hover:bg-muted/10">
                    <td className="px-3 py-2.5 text-muted-foreground">{i + 1}</td>
                    <td className="px-3 py-2.5 font-mono whitespace-nowrap text-muted-foreground">
                      {new Date(h.timestamp).toLocaleString()}
                    </td>
                    <td className="px-3 py-2.5 font-mono">{h.ip}</td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-1">
                        <span className="font-semibold">{h.country_code || "—"}</span>
                        {h.country && <span className="text-muted-foreground">{h.country}</span>}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-muted-foreground">{h.city || "—"}</td>
                    <td className="px-3 py-2.5 text-muted-foreground truncate max-w-[160px]">
                      {h.isp || h.org || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
